/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { ActiveSubscription, Product } from "@/adapters/productsAdapter";
import { License, Order } from "@/types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface UserState {
  id: string | null;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: {
    line1: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  isAuthenticated: boolean;
  billingDetails: {
    name: string;
    email: string;
    address: {
      line1: string;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
  } | null;
  products: Product[];
  theme: "system" | "light" | "dark";
  invoices: Order[];
  activeSubscriptions: ActiveSubscription[];
  deviceRemovedToggle: boolean;
  licenses: License[];
  newUser: boolean;
  breadpoint: string | null;
  userCurrency: string;
  // VA-12 (FCR Pass 11): Separate "not ready" from "not found". False while the
  // adaptation is in flight; true after it completes (success or error).
  dashboardCatalogReady: boolean;
}

const initialState: UserState = {
  id: "",
  name: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: {
    line1: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
  },
  isAuthenticated: false,
  billingDetails: null,
  products: [] as Product[],
  theme: "system",
  invoices: [],
  activeSubscriptions: [],
  deviceRemovedToggle: false,
  licenses: [],
  newUser: false,
  breadpoint: null,
  userCurrency: "USD",
  dashboardCatalogReady: false,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<{ id: string; name: string; email: string }>) {
      state.id = action.payload.id;
      state.name = action.payload.name;
      state.email = action.payload.email;
    },
    setUserCurrency(state, action: PayloadAction<string>) {
      state.userCurrency = action.payload;
    },
    updateUserDetails(
      state,
      action: PayloadAction<{
        name: string;
        email?: string;
        phone?: string;
        address?: Partial<UserState["address"]>;
      }>
    ) {
      const { name, email, phone, address } = action.payload;
      state.name = name;
      const [firstName, ...rest] = name.split(" ");
      state.firstName = firstName || name;
      state.lastName = rest.join(" ") || "";
      state.email = email || state.email;
      state.phone = phone || state.phone;
      if (address) {
        state.address = {
          ...state.address,
          ...address,
        };
      }
    },
    setBreadpoint(state, action: PayloadAction<string | null>) {
      state.breadpoint = action.payload;
    },
    setNewUser(state, action: PayloadAction<boolean>) {
      state.newUser = action.payload;
    },
    clearUser(state) {
      state.id = null;
      state.name = "";
      state.email = "";
      state.isAuthenticated = false;
      state.billingDetails = null;
      state.products = [];
      // VA-12: reset catalog ready on logout so the next account doesn't
      // briefly see the previous catalog treated as ready.
      state.dashboardCatalogReady = false;
      // VS-2 (S712): the two the catalogue also fills. Clearing products and
      // the ready flag while leaving these behind meant the next account --
      // or the same account after a sign-out and back in -- was rendered
      // against the PREVIOUS customer's subscriptions and invoices until the
      // new fetch landed. On a shared computer that is somebody else's billing
      // history on screen.
      state.activeSubscriptions = [];
      state.invoices = [];
    },
    setAuthentication(state, action: PayloadAction<boolean>) {
      state.isAuthenticated = action.payload;
    },
    setDeviceRemovedToggle(state) {
      state.deviceRemovedToggle = !state.deviceRemovedToggle;
    },
    setBillingDetails(state, action: PayloadAction<UserState["billingDetails"]>) {
      state.billingDetails = action.payload;
    },
    setProducts(state, action: PayloadAction<Product[]>) {
      state.products = action.payload;
    },
    // VA-12 (FCR Pass 11): Separate "not ready" from "not found". Set false
    // before adaptation starts, true in the finally block after it completes.
    setDashboardCatalogReady(state, action: PayloadAction<boolean>) {
      state.dashboardCatalogReady = action.payload;
    },
    setTheme(state, action: PayloadAction<"system" | "light" | "dark">) {
      state.theme = action.payload;
    },
    setInvoices(state, action: PayloadAction<Order[]>) {
      state.invoices = action.payload;
    },
    setActiveSubscriptions(state, action: PayloadAction<ActiveSubscription[]>) {
      state.activeSubscriptions = action.payload;
    },
  },
});

export const {
  setUser,
  clearUser,
  setAuthentication,
  setBillingDetails,
  setProducts,
  setDashboardCatalogReady,
  setTheme,
  setInvoices,
  setActiveSubscriptions,
  setDeviceRemovedToggle,
  setNewUser,
  setBreadpoint,
  updateUserDetails,
  setUserCurrency,
} = userSlice.actions;
export default userSlice.reducer;
